import React, { useState } from "react";
import { 
  Database, 
  Download, 
  ShieldCheck, 
  RefreshCw, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  FileJson, 
  Activity, 
  Loader2, 
  Server,
  Layers,
  Check
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { generateSystemBackup, runSystemIntegrityCheck } from "../services/backupService";

export default function BackupRecovery() {
  const { currentUser } = useAuth();
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupSuccess, setBackupSuccess] = useState(null);
  const [backupError, setBackupError] = useState("");

  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);

  async function handleExportBackup() {
    try {
      setBackupLoading(true);
      setBackupError("");
      setBackupSuccess(null);

      const res = await generateSystemBackup(currentUser?.email || "admin@jimini.com");
      setBackupSuccess(res);
    } catch (err) {
      console.error("Backup export failed:", err);
      setBackupError("Failed to export system backup snapshot. Please try again.");
    } finally {
      setBackupLoading(false);
    }
  }

  async function handleRunIntegrityAudit() {
    try {
      setAuditLoading(true);
      const res = await runSystemIntegrityCheck();
      setAuditResult(res);
    } catch (err) {
      console.error("Integrity check failed:", err);
    } finally {
      setAuditLoading(false);
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "PASS":
        return {
          icon: <CheckCircle2 size={16} style={{ color: "var(--success)" }} />,
          bg: "rgba(76, 175, 80, 0.15)",
          color: "var(--success)",
          border: "1px solid var(--success)"
        };
      case "WARN":
        return {
          icon: <AlertTriangle size={16} style={{ color: "#ff9800" }} />,
          bg: "rgba(255, 152, 0, 0.15)",
          color: "#ff9800",
          border: "1px solid #ff9800"
        };
      case "FAIL":
      default:
        return {
          icon: <XCircle size={16} style={{ color: "#ef4444" }} />,
          bg: "rgba(239, 68, 68, 0.15)",
          color: "#ef4444",
          border: "1px solid #ef4444"
        };
    }
  };

  return (
    <div className="animate-fade-in" style={{ padding: "1.5rem", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: "1.5rem" }}>
        <div className="page-header-info">
          <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <Database className="sidebar-logo-icon" style={{ color: "var(--primary)" }} />
            Backup & Disaster Recovery Manager
          </h1>
          <p className="page-subtitle" style={{ color: "var(--text-secondary)" }}>
            SOC 2 Type 2 (CC9.1) & ISO/IEC 27001 (A.12.3 / A.17) Business Continuity and Disaster Recovery controls.
          </p>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
        <div className="card glass-card" style={{ padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            <span>Database Health Score</span>
            <Activity size={18} style={{ color: "var(--success)" }} />
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: auditResult ? (auditResult.score >= 80 ? "var(--success)" : "#ff9800") : "var(--text-primary)" }}>
            {auditResult ? `${auditResult.score}%` : "100% Ready"}
          </div>
        </div>

        <div className="card glass-card" style={{ padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            <span>Monitored Collections</span>
            <Layers size={18} style={{ color: "var(--primary)" }} />
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "var(--text-primary)" }}>
            10 Primary
          </div>
        </div>

        <div className="card glass-card" style={{ padding: "1.25rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--text-secondary)", fontSize: "0.85rem", marginBottom: "0.5rem" }}>
            <span>QuickBooks API Status</span>
            <ShieldCheck size={18} style={{ color: "var(--primary)" }} />
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "var(--primary)" }}>
            Protected
          </div>
        </div>
      </div>

      {/* Main Grid: Backup Export & Integrity Auditor */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1.5rem" }}>
        {/* CARD 1: FULL SYSTEM BACKUP SNAPSHOT */}
        <div className="card glass-card" style={{ padding: "2rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
            <div>
              <h2 style={{ fontSize: "1.25rem", fontWeight: 700, margin: "0 0 0.35rem 0", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <FileJson size={22} style={{ color: "var(--primary)" }} />
                Enterprise Database Snapshot Generator
              </h2>
              <p className="text-secondary" style={{ fontSize: "0.85rem", margin: 0, maxWidth: "680px" }}>
                Generates a complete, structured JSON backup bundle across all 10 core collections. All sensitive credentials, OAuth tokens, and TOTP secrets are automatically sanitized before export.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleExportBackup}
              disabled={backupLoading}
              style={{ padding: "0.6rem 1.25rem" }}
            >
              {backupLoading ? (
                <>
                  <Loader2 className="animate-spin" size={16} style={{ marginRight: "0.5rem" }} />
                  Generating Snapshot...
                </>
              ) : (
                <>
                  <Download size={16} style={{ marginRight: "0.5rem" }} />
                  Export System Backup (.json)
                </>
              )}
            </button>
          </div>

          {backupError && (
            <div className="alert alert-danger" style={{ marginBottom: "1rem" }}>
              <AlertTriangle size={16} style={{ marginRight: "0.5rem" }} />
              {backupError}
            </div>
          )}

          {backupSuccess && (
            <div className="alert alert-success" style={{ marginBottom: "1rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontWeight: 600 }}>
                <Check size={18} />
                <span>Backup Generated Successfully! Total {backupSuccess.totalRecords} records exported.</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", fontSize: "0.8rem", marginTop: "0.25rem" }}>
                {Object.entries(backupSuccess.recordCounts || {}).map(([col, count]) => (
                  <span key={col} style={{ background: "rgba(255,255,255,0.1)", padding: "0.15rem 0.5rem", borderRadius: "4px" }}>
                    {col}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CARD 2: DISASTER RECOVERY & SCHEMA INTEGRITY AUDITOR */}
        <div className="card glass-card" style={{ padding: "2rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
            <div>
              <h2 style={{ fontSize: "1.25rem", fontWeight: 700, margin: "0 0 0.35rem 0", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <ShieldCheck size={22} style={{ color: "var(--success)" }} />
                Disaster Recovery & Schema Integrity Auditor
              </h2>
              <p className="text-secondary" style={{ fontSize: "0.85rem", margin: 0, maxWidth: "680px" }}>
                Performs automated non-destructive schema validation, orphan inventory detection, customer linkage checks, and pricing calculation sanity checks.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleRunIntegrityAudit}
              disabled={auditLoading}
              style={{ padding: "0.6rem 1.25rem" }}
            >
              {auditLoading ? (
                <>
                  <Loader2 className="animate-spin" size={16} style={{ marginRight: "0.5rem" }} />
                  Auditing Collections...
                </>
              ) : (
                <>
                  <RefreshCw size={16} style={{ marginRight: "0.5rem" }} />
                  Run DR Integrity Audit
                </>
              )}
            </button>
          </div>

          {auditResult ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {auditResult.checks.map((check, idx) => {
                const badge = getStatusBadge(check.status);
                return (
                  <div
                    key={idx}
                    style={{
                      background: "var(--bg-main)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "8px",
                      padding: "1rem 1.25rem",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "1rem"
                    }}
                  >
                    <div>
                      <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "var(--text-primary)", marginBottom: "0.25rem" }}>
                        {check.name}
                      </div>
                      <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                        {check.message}
                      </div>
                    </div>

                    <span 
                      style={{ 
                        display: "inline-flex", 
                        alignItems: "center", 
                        gap: "0.35rem", 
                        padding: "0.3rem 0.75rem", 
                        borderRadius: "12px", 
                        fontSize: "0.8rem", 
                        fontWeight: 700,
                        backgroundColor: badge.bg,
                        color: badge.color,
                        border: badge.border,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {badge.icon}
                      {check.status}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-secondary)", background: "var(--bg-main)", borderRadius: "8px", border: "1px solid var(--border-color)" }}>
              <Activity size={36} style={{ opacity: 0.3, margin: "0 auto 0.75rem auto" }} />
              <p style={{ fontSize: "0.9rem", margin: 0 }}>Click "Run DR Integrity Audit" to execute database integrity and schema validation checks.</p>
            </div>
          )}
        </div>

        {/* CARD 3: THIRD-PARTY INTEGRATION SAFEGUARDS (QUICKBOOKS ONLINE) */}
        <div className="card glass-card" style={{ padding: "2rem" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 700, margin: "0 0 0.35rem 0", color: "var(--text-primary)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Server size={22} style={{ color: "var(--primary)" }} />
            Third-Party Integration Safeguards (QuickBooks Online)
          </h2>
          <p className="text-secondary" style={{ fontSize: "0.85rem", marginBottom: "1.25rem" }}>
            Monitors API endpoint connectivity and Content Security Policy (CSP) whitelisting status for external accounting integrations.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
            <div style={{ background: "var(--bg-main)", border: "1px solid var(--border-color)", borderRadius: "8px", padding: "1rem" }}>
              <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: "0.25rem" }}>CSP Whitelisted Endpoints</div>
              <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--success)" }}>
                *.api.intuit.com / *.qbo.intuit.com
              </div>
            </div>

            <div style={{ background: "var(--bg-main)", border: "1px solid var(--border-color)", borderRadius: "8px", padding: "1rem" }}>
              <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginBottom: "0.25rem" }}>API Outage Circuit Breaker</div>
              <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--primary)" }}>
                Active (Non-Blocking Fail-Safe)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
